import { FC, useState, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { Icon } from '@eon-home/react-library';

import { Scope } from '@tools/enums';
import { Prompt } from '@components';
import { WithScopes } from '@hocs/with-scopes';
import { SpotPriceType } from '@tools/types';
import { SpotPrice, BillingRegion } from '@containers/emobility';
import {
    useBillingRegion,
    useShouldShowRegionSelector,
    useWallboxConfigId,
} from '@store/selectors';

import ArrowRight from '@assets/icons/misc/arrow-right.svg';
import { getWallboxChargingModesData, getSpotPrice } from '@store/actions';

interface Props {
    type?: SpotPriceType;
}

export const SpotPriceTrigger: FC<Readonly<Props>> = ({ type }: Props) => {
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const region = useBillingRegion().data;
    const configurationId = useWallboxConfigId();
    const [showSpotPrice, setShowSpotPrice] = useState(false);
    const showRegionSelector = useShouldShowRegionSelector();

    const shouldShowSpotPrice = useMemo(
        () => !!region || !showRegionSelector,
        [region, showRegionSelector],
    );

    return (
        <>
            <button
                type="button"
                onClick={() => {
                    setShowSpotPrice(!showSpotPrice);
                }}
                className="c-arrow-link"
            >
                <Icon src={ArrowRight} />

                <span>
                    {shouldShowSpotPrice
                        ? t('Check spot price')
                        : t('Set region')}
                </span>
            </button>

            {showSpotPrice && (
                <Prompt
                    small
                    onClose={() => {
                        setShowSpotPrice(!showSpotPrice);
                    }}
                    isOverlaying
                    className="c-spot-price__wrapper"
                >
                    {shouldShowSpotPrice ? (
                        <SpotPrice type={type} />
                    ) : (
                        <BillingRegion
                            onSelect={() => {
                                dispatch(getSpotPrice());
                                dispatch(
                                    getWallboxChargingModesData(
                                        configurationId || '',
                                        true,
                                    ),
                                );
                            }}
                        />
                    )}
                </Prompt>
            )}
        </>
    );
};

export default (props: Props) => (
    <WithScopes requiredScopes={[Scope.BILLING_REGION_READ]}>
        <SpotPriceTrigger {...props} />
    </WithScopes>
);
